import React from "react";
import PropTypes from "prop-types";
import { animated, useSpring, config } from "@react-spring/web";
import { InfoIcon } from "../../components/Icons";
import i18n from "../../Helpers/i18n";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import useBodyScrollLock from "~/assets/js/Helpers/hooks/useBodyScrollLock";

const Popup = ({
    title,
    subtitle,
    customStyle = "",
    open,
    handleClose,
    handleInfo,
    isInfo = false,
    children,
    notification,
    position = "bottom",
    draggable,
    isClosable,
}) => {
    // Prevents body scroll when popup is open
    useBodyScrollLock(open);
    const { isDesktop } = useViewport();
    const displayDraggable = !isDesktop && draggable;

    const transitionBottom = useSpring({
        y: open ? 0 : 500,
        config: { ...config.molasses, duration: 300 },
    });

    const transitionSide = useSpring({
        x: open ? 0 : 500,
        config: { ...config.molasses, duration: 300 },
    });

    const setHeight = (height) => {
        const windowHeight = window.innerHeight;
        const popupHeight = windowHeight - height;
        document.getElementById("popup").style.height = `${popupHeight}px`;
    };

    const onDragEnd = (height) => {
        const windowHeight = window.innerHeight;
        const popupHeight = windowHeight - height;

        if (popupHeight < 300) {
            handleClose();
        } else {
            document.getElementById("popup").style.height = "unset";
        }
    };

    const classNames = `popup__wrapper popup__position-${position} ${customStyle}`;
    const noConfig = title === undefined && subtitle === undefined && !isInfo;
    if (open) {
        return (
            <>
                <div
                    className="popup__backdrop"
                    onClick={isClosable ? handleClose : undefined}
                ></div>
                <animated.div
                    style={
                        position === "bottom"
                            ? transitionBottom
                            : transitionSide
                    }
                    id="popup"
                    className={classNames}
                >
                    {notification && notification}

                    {displayDraggable && (
                        <div
                            className="draggable"
                            onTouchMove={(event) => {
                                event.persist();
                                setHeight(event.touches[0].clientY);
                            }}
                            onTouchEnd={(event) => {
                                event.persist();
                                onDragEnd(event.changedTouches[0].clientY);
                            }}
                        ></div>
                    )}

                    {!noConfig && (
                        <div className="popup__top">
                            <div className="popup__header">
                                <div>
                                    {title && (
                                        <span className="popup__title">
                                            {title}
                                        </span>
                                    )}
                                    {subtitle && (
                                        <span className="popup__subtitle">
                                            {subtitle}
                                        </span>
                                    )}
                                </div>
                                {isInfo ? (
                                    <div
                                        className="notification__icon"
                                        onClick={handleInfo}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <InfoIcon />
                                    </div>
                                ) : (
                                    handleClose && (
                                        <span
                                            className="popup-close"
                                            onClick={handleClose}
                                            style={{ cursor: "pointer" }}
                                        >
                                            {i18n.t("popup.close")}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                    )}
                    <div className="popup__content">{children}</div>
                </animated.div>
            </>
        );
    }
    // Closed popup
    return null;
};

Popup.propTypes = {
    title: PropTypes.string,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    isInfo: PropTypes.bool,
    notification: PropTypes.node,
    customStyle: PropTypes.string,
    subtitle: PropTypes.string,
    handleInfo: PropTypes.func,
    position: PropTypes.oneOf(["bottom", "side", "top"]),
    draggable: PropTypes.bool,
    isClosable: PropTypes.bool,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};

Popup.defaultProps = {
    handleClose: () => {},
    handleInfo: () => {},
};

export default Popup;
